/**
 * headerFooterZones — composing 3-zone (left/center/right) header & footer
 * content into the single HTML strings PaginationPlus can render, plus
 * normalization of every config shape we have ever persisted.
 *
 * PaginationPlus only offers two slots (headerLeft/headerRight, floated
 * left/right). To get a real center zone we render ALL zones into one
 * grid wrapper (`.hfz`) passed as the LEFT slot, with CSS making that slot
 * span the full width (see TipTapEditor.css).
 *
 * Page-number tokens: the library replaces only the FIRST "{page}" in a
 * slot, so we pre-render every token to a counter span ourselves.
 * GOTCHA (inherited from the existing code): headers render before the
 * page-break counter increment and footers after it, so headers need the
 * `rm-page-number-plus` counter (starts at 1) while footers use
 * `rm-page-number` (starts at 0). Verified visually — if numbers ever look
 * off by one, this is the knob.
 */

import type {
  HeaderFooterSection,
  ParsedHeaderFooterConfig,
} from "./ooxmlParser/types";

export const HEADER_PAGE_NUM_CLASS = "rm-page-number-plus";
export const FOOTER_PAGE_NUM_CLASS = "rm-page-number";

export function emptySection(): HeaderFooterSection {
  return { left: "", center: "", right: "" };
}

const BLOCKED_TAGS = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "link",
  "meta",
  "base",
  "form",
]);

/**
 * Sanitize zone HTML before it reaches a raw DOM sink. Zone strings are
 * persisted in document meta and round-trip through the save API and DOCX
 * import — i.e. they are USER-CONTROLLED and rendered via innerHTML /
 * document.write, completely outside ProseMirror's schema sanitization.
 * Strips active elements, on* handlers and javascript: URLs; re-serializing
 * through the parser also normalizes any attribute-breakout attempts.
 */
export function sanitizeZoneHtml(html: string | undefined): string {
  if (!html) return "";
  if (!html.includes("<")) return html;
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  } catch {
    return "";
  }
  const walk = (el: Element) => {
    for (const child of Array.from(el.children)) {
      if (BLOCKED_TAGS.has(child.tagName.toLowerCase())) {
        child.remove();
        continue;
      }
      for (const attr of Array.from(child.attributes)) {
        const name = attr.name.toLowerCase();
        if (name.startsWith("on")) {
          child.removeAttribute(attr.name);
        } else if (
          (name === "href" || name === "src" || name === "xlink:href") &&
          /^\s*(javascript|vbscript|data:text\/html)/i.test(attr.value)
        ) {
          child.removeAttribute(attr.name);
        }
      }
      // Header/footer logos uploaded to the backend are served cross-origin
      // behind helmet's CORP: same-origin; a plain <img> in the composed zone
      // HTML is blocked. Load backend-proxy images with credentialed CORS (see
      // credentialed CORS so the logo renders in the overlay and on every page band.
      if (child.tagName.toLowerCase() === "img") {
        const src = child.getAttribute("src") || "";
        if (/\/api\/image-proxy\//.test(src)) {
          child.setAttribute("crossorigin", "use-credentials");
        }
      }
      walk(child);
    }
  };
  walk(doc.body);
  return doc.body.innerHTML;
}

function sectionIsEmpty(section: HeaderFooterSection | undefined): boolean {
  if (!section) return true;
  const strip = (html: string | undefined) =>
    (html || "").replace(/<[^>]*>/g, "").trim();
  return (
    !strip(section.left) &&
    !strip(section.center) &&
    !strip(section.right) &&
    !(section.left || "").includes("{page}") &&
    !(section.center || "").includes("{page}") &&
    !(section.right || "").includes("{page}") &&
    !(section.left || "").includes("<img") &&
    !(section.center || "").includes("<img") &&
    !(section.right || "").includes("<img")
  );
}

/** Replace every {page} token with the live counter span for the area. */
export function renderPageTokens(
  html: string,
  pageNumberClass: string
): string {
  return (html || "").replace(
    /\{page\}/g,
    `<span class="${pageNumberClass}"></span>`
  );
}

/**
 * Compose a section's three zones into one HTML string for the library's
 * LEFT slot (the right slot stays empty). Returns "" when the section has
 * no visible content.
 */
export function composeSectionHtml(
  section: HeaderFooterSection | undefined,
  pageNumberClass: string
): string {
  if (sectionIsEmpty(section)) return "";
  const left = renderPageTokens(
    sanitizeZoneHtml(section?.left),
    pageNumberClass
  );
  const center = renderPageTokens(
    sanitizeZoneHtml(section?.center),
    pageNumberClass
  );
  const right = renderPageTokens(
    sanitizeZoneHtml(section?.right),
    pageNumberClass
  );
  return (
    `<div class="hfz">` +
    `<div class="hfz-zone hfz-l">${left}</div>` +
    `<div class="hfz-zone hfz-c">${center}</div>` +
    `<div class="hfz-zone hfz-r">${right}</div>` +
    `</div>`
  );
}

/**
 * Normalize any persisted/imported header-footer shape into a fully
 * populated ParsedHeaderFooterConfig:
 *   - ParsedHeaderFooterConfig (sections with left/center/right) — current
 *   - legacy flat shape ({ headerLeft, headerRight, footerLeft, ... }) —
 *     pre-rebuild saves
 *   - null/undefined — fresh document (footer defaults to "{page}" on the
 *     right, matching the editor's historical default)
 */
export function normalizeParsedConfig(raw: unknown): ParsedHeaderFooterConfig {
  const base: ParsedHeaderFooterConfig = {
    defaultHeader: emptySection(),
    defaultFooter: { ...emptySection(), right: "{page}" },
    hasTitlePage: false,
  };
  if (!raw || typeof raw !== "object") return base;

  const obj = raw as Record<string, any>;

  // Legacy flat shape (from the old modal / old persisted meta)
  if (
    typeof obj.headerLeft === "string" ||
    typeof obj.footerLeft === "string" ||
    typeof obj.headerRight === "string" ||
    typeof obj.footerRight === "string"
  ) {
    const cfg: ParsedHeaderFooterConfig = {
      defaultHeader: {
        left: obj.headerLeft || "",
        center: obj.headerCenter || "",
        right: obj.headerRight || "",
      },
      defaultFooter: {
        left: obj.footerLeft || "",
        center: obj.footerCenter || "",
        right: obj.footerRight ?? "{page}",
      },
      hasTitlePage: !!obj.differentFirstPage,
    };
    if (cfg.hasTitlePage) {
      cfg.firstPageHeader = {
        left: obj.firstPageHeaderLeft || "",
        center: obj.firstPageHeaderCenter || "",
        right: obj.firstPageHeaderRight || "",
      };
      cfg.firstPageFooter = {
        left: obj.firstPageFooterLeft || "",
        center: obj.firstPageFooterCenter || "",
        right: obj.firstPageFooterRight || "",
      };
    }
    return cfg;
  }

  // Section shape (parser output or rich saves)
  const normSection = (
    s: any,
    fallback: HeaderFooterSection
  ): HeaderFooterSection =>
    s && typeof s === "object"
      ? { left: s.left || "", center: s.center || "", right: s.right || "" }
      : fallback;

  const cfg: ParsedHeaderFooterConfig = {
    defaultHeader: normSection(obj.defaultHeader, emptySection()),
    defaultFooter: normSection(obj.defaultFooter, {
      ...emptySection(),
      right: "{page}",
    }),
    hasTitlePage: !!obj.hasTitlePage,
  };
  if (obj.firstPageHeader) {
    cfg.firstPageHeader = normSection(obj.firstPageHeader, emptySection());
  }
  if (obj.firstPageFooter) {
    cfg.firstPageFooter = normSection(obj.firstPageFooter, emptySection());
  }
  return cfg;
}
